import { notNull, omit } from "@msys/common";
import {
  DebouncedSearchInput,
  ListHeader,
  ListHeaderActiveFilters,
  ListHeaderActiveFiltersActionButton,
  MenuButton,
  MenuItemWithIcon,
  ModalOpenButton,
  useFormatting,
  useScreenWidth,
} from "@msys/ui";
import {
  Checklist as ChecklistIcon,
  Settings as SettingsIcon,
  ViewKanban as ViewKanbanIcon,
} from "@mui/icons-material";
import { Divider, Stack, Typography } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import React from "react";
import {
  namedOperations,
  OrganisationProjectsSorting,
  ProjectStateMachineStatus,
} from "../../../clients/graphqlTypes.js";
import { RestrictedByOrganisationPermissionWithDebug } from "../../auth/RestrictedByOrganisationPermission.js";
import { useUserData } from "../../auth/useUserData.js";
import { SwitchCollectionViewButton } from "../../commons/button/SwitchCollectionViewButton.js";
import { FilterButton } from "../../commons/filters/FilterButton.js";
import { useFiltersAndPagination } from "../../commons/filters/useFiltersAndPagination.js";
import {
  CollectionView,
  useCollectionViewWithMobile,
} from "../../commons/hooks/useCollectionView.js";
import { Page, PageTopbarItem } from "../../commons/layout/Page.js";
import { PageContainer } from "../../commons/layout/PageContainer.js";
import { ImportCustomGaebP84Button } from "../../features/importExport/gaebImport/ImportCustomGaebP84Button.js";
import { ImportXiopdButton } from "../../features/importExport/xiopdImport/ImportXiopdButton.js";
import { ImportVisibilityKeys } from "../../features/organisations/boxes/OrganisationProjectImportSettingsBox.js";
import { CreateProjectButton } from "../../features/projects/buttons/CreateProjectButton.js";
import { ProjectListItemDisplayConfig } from "../../features/projects/components/ProjectListItem.js";
import {
  defaultListItemDisplayConfig,
  ProjectsListItemVisibilitySettingsModal,
} from "../../features/projects/components/ProjectsListItemVisibilitySettingsModal.js";
import { FilterProjectAssigneesButton } from "../../features/projects/filters/ProjectAssignees.js";
import { FilterProjectBudgetButton } from "../../features/projects/filters/ProjectBudget.js";
import { ProjectsFilterModal } from "../../features/projects/ProjectsFilterModal.js";
import { ProjectsKanban } from "../../features/projects/ProjectsKanban.js";
import {
  DEFAULT_SORTING,
  type Filters,
  ProjectsList,
} from "../../features/projects/ProjectsList.js";
import { getProjectFilterChipsFactory } from "../../features/projects/renderProjectsFilterChips.js";
import { PROJECT_FILTER_MAP } from "../../features/projects/useProjectSources.js";
import { useHasSapS4HanaIntegration } from "../../features/sap-s4-hana/hooks.js";
import { ImportS4HanaSalesQuotationButton } from "../../features/sap-s4-hana/ImportS4HanaSalesQuotationButton.js";
import { TeamsFilterButton } from "../../features/teams/filters/TeamsFilter.js";
import { useTeamFilterDefaultSeeding } from "../../features/teams/filters/useTeamFilterDefaultSeeding.js";
import { useDataGridStateStore } from "../../features/users/useDataGridStateStore.js";
import { useListViewCustomQuickFilters } from "../../features/users/useListViewCustomQuickFilters.js";
import { useUserPreference } from "../../features/users/useUserPreference.js";
import { useVisibilityStore } from "../../features/users/useVisibilityStore.js";

const DEFAULT_ITEMS_PER_PAGE = 25;
const ALLOWED_VIEWS: CollectionView[] = ["table", "list", "kanban"];

interface Props {
  submenuItems: PageTopbarItem[];
}

export const ProjectsCurrent = ({ submenuItems }: Props) => {
  const { t } = useTranslate([
    "Projects",
    "Opportunities",
    "Tickets",
    "Global",
  ]);
  const viewer = useUserData().currentUser!;
  const hasSapS4HanaIntegration = useHasSapS4HanaIntegration();

  const { getFormattedPrice, getFormattedFloat } = useFormatting();
  const { isMinTablet, isMaxPhone } = useScreenWidth();

  const [isConfiguring, setIsConfiguring] = React.useState<boolean>(false);

  const [activeView, setActiveView] =
    useCollectionViewWithMobile<CollectionView>(
      "projects-current",
      "table",
      "list"
    );

  const allowedStates: ProjectStateMachineStatus[] = React.useMemo(
    () =>
      viewer.organisation.isClientOrganisation
        ? ["contracted", "opportunity"]
        : ["contracted"],
    [viewer.organisation.isClientOrganisation]
  );

  const {
    offset,
    limit,
    paginationModel,
    setPaginationModel,
    filters,
    setFilters,
    resetFilters,
    sorting,
    setSorting,
    toRemoveParams,
  } = useFiltersAndPagination<OrganisationProjectsSorting, Filters>(
    {},
    DEFAULT_SORTING,
    DEFAULT_ITEMS_PER_PAGE
  );

  useTeamFilterDefaultSeeding<Filters, "filterTeamIds">({
    filters,
    setFilters,
    viewerActiveTeamIds: viewer.teams.map(t => t.id),
    filterKey: "filterTeamIds",
  });

  const listViewName = "ProjectsCurrent";

  const getActiveProjectFilterChips = getProjectFilterChipsFactory({
    viewer,
    t,
    allowedStates,
    getFormattedFloat,
    showPhasesChip: activeView !== "kanban",
  });

  const { customQuickFilterSelect, addCustomFilterButton } =
    useListViewCustomQuickFilters<Filters>(
      listViewName,
      filters,
      setFilters,
      [
        {
          filterName: t("Relevant to me", { ns: "Opportunities" }),
          filterValue: {
            filterRelevantToUserIds: [viewer.id],
          },
        },
      ],
      getActiveProjectFilterChips
    );

  const baseVariables = React.useMemo(
    () => ({
      offset,
      limit,
      sorting: DEFAULT_SORTING,
    }),
    [limit, offset]
  );

  const variables = React.useMemo(
    () => ({
      ...baseVariables,
      ...filters,
      filterBySources: filters.sources
        ? filters.sources.map(s => PROJECT_FILTER_MAP[s]).flat(1)
        : undefined,
      filterIncludeState: filters.filterIncludeState?.length
        ? filters.filterIncludeState
        : allowedStates,
      sorting,
    }),
    [allowedStates, baseVariables, filters, sorting]
  );

  const stateStore = useDataGridStateStore(listViewName);

  const importVisibilityStore = useVisibilityStore<ImportVisibilityKeys>(
    `${listViewName}-Import`
  );
  const listItemDisplayConfigStore =
    useUserPreference<ProjectListItemDisplayConfig>(
      `DisplayConfig-${listViewName}`,
      defaultListItemDisplayConfig
    );

  const menuItems = !importVisibilityStore.loading
    ? [
        <RestrictedByOrganisationPermissionWithDebug
          permission="CREATE_PROJECT"
          key="import-gaeb"
        >
          {importVisibilityStore.value.gaeb !== false ? (
            <ImportCustomGaebP84Button
              projectParams={{ isTentative: false }}
              refetchQueries={[namedOperations.Query.ProjectsList]}
            />
          ) : null}
        </RestrictedByOrganisationPermissionWithDebug>,
        <RestrictedByOrganisationPermissionWithDebug
          permission="CREATE_PROJECT"
          key="import-xiopd"
        >
          {importVisibilityStore.value.xiopd !== false ? (
            <ImportXiopdButton
              projectParams={{ isTentative: false }}
              refetchQueries={[namedOperations.Query.ProjectsList]}
            />
          ) : null}
        </RestrictedByOrganisationPermissionWithDebug>,
        hasSapS4HanaIntegration ? (
          <RestrictedByOrganisationPermissionWithDebug
            permission="CREATE_PROJECT"
            key="import-s4-hana-salesquoation"
          >
            <ImportS4HanaSalesQuotationButton
              projectId={null}
              refetchQueries={[namedOperations.Query.ProjectsList]}
            />
          </RestrictedByOrganisationPermissionWithDebug>
        ) : null,
      ].filter(notNull)
    : [];

  const settingsItems = [
    activeView !== "table" ? (
      <ModalOpenButton
        key="projects-list-item-visibility-settings"
        Modal={ProjectsListItemVisibilitySettingsModal}
        modalProps={{
          listItemDisplayConfigStore,
        }}
      >
        <MenuItemWithIcon icon={<ChecklistIcon />}>
          {t("Projects view settings", { ns: "Projects" })}
        </MenuItemWithIcon>
      </ModalOpenButton>
    ) : null,
    activeView === "kanban" ? (
      <RestrictedByOrganisationPermissionWithDebug
        key="projects-configure-pipeline"
        permission="MANAGE_ORG"
      >
        <MenuItemWithIcon
          disabled={isConfiguring}
          icon={<ViewKanbanIcon />}
          onClick={() => {
            setIsConfiguring(true);
          }}
        >
          {t("Configure pipeline", { ns: "Opportunities" })}
        </MenuItemWithIcon>
      </RestrictedByOrganisationPermissionWithDebug>
    ) : null,
  ].filter(notNull);

  const allowedViewsForViewer = ALLOWED_VIEWS.filter(view => {
    if (viewer.organisation.isClientOrganisation && view === "kanban")
      return false;
    return true;
  });

  const mobileOverflowItems: React.ReactNode[] = (() => {
    if (!isMaxPhone) return [];
    const viewItems: React.ReactNode[] =
      allowedViewsForViewer.length > 0
        ? [
            <SwitchCollectionViewButton
              key="mobile-overflow-view-options"
              renderAs="menuItems"
              allowedViews={allowedViewsForViewer}
              activeView={activeView}
              setActiveView={setActiveView}
            />,
          ]
        : [];
    const groups = [viewItems, settingsItems, menuItems].filter(
      g => g.length > 0
    );
    const items: React.ReactNode[] = [];
    groups.forEach((group, idx) => {
      if (idx > 0)
        items.push(<Divider key={`mobile-overflow-divider-${idx}`} />);
      items.push(...group);
    });
    return items;
  })();

  const activeFilterChips = getActiveProjectFilterChips({
    filters,
    setFilters,
  });

  return (
    <Page
      title={t("Current", { ns: "Projects" })}
      subtitle={t("Projects", { ns: "Projects" })}
      submenuItems={submenuItems}
    >
      <PageContainer $fullHeight={activeView === "kanban"}>
        <ListHeader
          QuickFilter={
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              flexShrink={1}
              flexGrow={0}
            >
              {isMinTablet ? (
                <>
                  <FilterProjectBudgetButton
                    filters={filters}
                    setFilters={setFilters}
                    variables={variables}
                  />
                  <FilterProjectAssigneesButton
                    unassigned={filters.filterUnassigned ?? false}
                    setUnassigned={unassigned =>
                      setFilters(filters => ({
                        ...filters,
                        filterUnassigned: unassigned || undefined,
                      }))
                    }
                    value={filters.filterAssigneeIds ?? []}
                    setValue={(newValue: string[]) =>
                      setFilters(filters => ({
                        ...filters,
                        filterAssigneeIds: newValue,
                      }))
                    }
                  />
                  <TeamsFilterButton
                    value={filters.filterTeamIds ?? []}
                    setValue={newValue =>
                      setFilters(state =>
                        newValue.length > 0
                          ? { ...state, filterTeamIds: newValue }
                          : omit(state, "filterTeamIds")
                      )
                    }
                  />
                </>
              ) : undefined}
              {customQuickFilterSelect}
            </Stack>
          }
          SwitchViewButton={
            !isMaxPhone ? (
              <SwitchCollectionViewButton
                allowedViews={allowedViewsForViewer}
                activeView={activeView}
                setActiveView={setActiveView}
              />
            ) : undefined
          }
          CreateButton={
            <RestrictedByOrganisationPermissionWithDebug permission="CREATE_PROJECT">
              <CreateProjectButton
                pageName="Projects"
                type="button"
                mobileVariant="filled"
              />
            </RestrictedByOrganisationPermissionWithDebug>
          }
          FilterButton={
            <ModalOpenButton
              Modal={ProjectsFilterModal}
              modalProps={{
                sorting,
                setSorting,
                filters,
                setFilters,
                resetFilters,
                allowedStates,
                showPhasesFilter: activeView !== "kanban",
                showSorting: activeView !== "kanban",
              }}
            >
              <FilterButton />
            </ModalOpenButton>
          }
          SearchField={
            <DebouncedSearchInput
              placeholder={t("Search", { ns: "Global" })}
              defaultValue={filters.filterSearchTerm}
              onChangeSearchTerm={(newValue: string) => {
                setFilters(filters => ({
                  ...filters,
                  filterSearchTerm: newValue,
                }));
              }}
            />
          }
          ActiveFilterChips={
            <ListHeaderActiveFilters
              activeFilterChips={activeFilterChips}
              collapsedContent={
                <Typography variant="body2">
                  {t("Active filters", { ns: "Global" })}
                  <Typography
                    variant="body2"
                    component="span"
                    marginLeft={1}
                    fontWeight="bold"
                  >
                    {t("{count} results", {
                      ns: "Global",
                      count: activeFilterChips.length,
                    })}
                  </Typography>
                </Typography>
              }
              clearAllButton={
                <ListHeaderActiveFiltersActionButton
                  label={t("Clear all", { ns: "Global" })}
                  onClick={resetFilters}
                />
              }
              saveFiltersButton={addCustomFilterButton}
            />
          }
          ActionButtons={
            !isMaxPhone && settingsItems.length > 0 ? (
              <MenuButton Icon={<SettingsIcon />}>{settingsItems}</MenuButton>
            ) : undefined
          }
          MenuButton={
            isMaxPhone ? (
              mobileOverflowItems.length > 0 ? (
                <MenuButton>{mobileOverflowItems}</MenuButton>
              ) : undefined
            ) : menuItems.length > 0 ? (
              <MenuButton>{menuItems}</MenuButton>
            ) : undefined
          }
          mb={2}
        />
        {!listItemDisplayConfigStore.loading &&
          (activeView === "kanban" ? (
            <ProjectsKanban
              variables={variables}
              displayConfig={listItemDisplayConfigStore.value}
              isConfiguring={isConfiguring}
              setIsConfiguring={setIsConfiguring}
            />
          ) : (
            <ProjectsList
              sorting={sorting}
              setSorting={setSorting}
              activeView={activeView}
              pathToPage={"/projects/current"}
              paginationModel={paginationModel}
              setPaginationModel={setPaginationModel}
              showStatus={allowedStates.length > 1}
              showPhase={true}
              stateStore={stateStore}
              variables={variables}
              displayConfig={listItemDisplayConfigStore.value}
            />
          ))}
      </PageContainer>
    </Page>
  );
};
